import cn from "@/v2-ui/utils/utils.cn"
import Select from "@/v2-ui/select"
import CountryFlag from "@/v2-backoffice-client/country/CountryFlag"
import { useCountryQuery } from "@/v2-backoffice-client/country/country.query"

// Radix Select forbids an empty-string item value, so "no country chosen"
// carries this sentinel and maps back to null on the way out.
const NO_COUNTRY_VALUE = "none"

export type CountryPickerProps = {
  className?: string
  value: number | null
  onValueChange(value: number | null): void
  disabled?: boolean
  title?: string
  // Label for the "no country" option. Omit to drop that option entirely
  // and force a choice.
  emptyLabel?: string
  placeholder?: string
}

// Picks one of the countries the platform holds data for. The option list
// is the catalog's supported set, so it can't offer a country a project
// would then be rejected for.
function CountryPicker(props: CountryPickerProps) {
  const {
    className,
    value,
    onValueChange,
    disabled,
    title,
    emptyLabel,
    placeholder,
  } = props

  const { data, loading } = useCountryQuery()
  const countries = data?.getCountries ?? []

  // A stored country the catalog no longer offers — supported-ness changes
  // over time — matches no item, and Radix would render an empty trigger
  // for it. Show the bare code instead, like CountryLabel does: the value
  // is real and the operator overwrites it only by choosing another.
  const unknown =
    value !== null &&
    !loading &&
    !countries.some((country) => country.Code === value)

  return (
    <Select
      value={value === null ? NO_COUNTRY_VALUE : String(value)}
      disabled={disabled || loading}
      onValueChange={(next) => {
        onValueChange(next === NO_COUNTRY_VALUE ? null : Number(next))
      }}
    >
      <Select.Trigger
        size="md"
        className={cn("min-w-[240px]", className)}
        title={title}
      >
        <Select.Value
          placeholder={loading ? "Loading countries..." : placeholder}
        >
          {unknown ? `#${value}` : undefined}
        </Select.Value>
      </Select.Trigger>
      <Select.Content>
        {emptyLabel && (
          <Select.Item value={NO_COUNTRY_VALUE}>{emptyLabel}</Select.Item>
        )}
        {countries.map((country) => (
          <Select.Item key={country.Code} value={String(country.Code)}>
            <span className="flex items-center gap-2">
              <CountryFlag alpha2={country.Alpha2} name={country.Name} />
              {country.Name ?? country.Alpha2}
            </span>
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}

export default CountryPicker
